import React, { useEffect, useRef, useState } from 'react';
import { api } from '../api.js';
import { useI18n } from '../i18n/index.jsx';
import Icon from '../components/Icon.jsx';
import './tutorials.css';

// Tutorial videos (Admin > Contenu du site > Aide / Help). Nothing loads
// until the visitor clicks: cards show the poster only, the player opens in
// a pop-up (Esc closes it) with subtitles in the visitor's language. The
// video waits for the visitor to press play (no autoplay); onPlay tells the
// page it was watched ("until watched" placements, TutorialSlot.jsx).

export const fmtDuration = (s, t) => (s ? (s % 60 === 0 || s >= 120 ? t('help.minutes', { n: Math.round(s / 60) }) : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`) : '');
const pick = (obj, lang) => obj?.[lang] || obj?.[lang === 'fr' ? 'en' : 'fr'] || null;
export const tutText = (tut, key, lang) => tut[key][lang] || tut[key][lang === 'fr' ? 'en' : 'fr'];

export function useTutorials(place) {
  const [list, setList] = useState(null);
  useEffect(() => {
    let alive = true;
    api(`/tutorials?place=${place}`).then((r) => { if (alive) setList(r); }).catch(() => { if (alive) setList([]); });
    return () => { alive = false; };
  }, [place]);
  return list;
}

export function TutorialPlayer({ tut, onClose, onPlay }) {
  const { t, lang } = useI18n();
  const video = pick(tut.video, lang);
  const poster = pick(tut.poster, lang);
  const ref = useRef(null);
  const closeRef = useRef(null);
  useEffect(() => {
    const prev = document.activeElement;
    closeRef.current?.focus();
    const k = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', k);
    document.body.classList.add('tut-open');
    return () => { document.removeEventListener('keydown', k); document.body.classList.remove('tut-open'); prev?.focus?.(); };
  }, [onClose]);
  // YouTube (no play event from the frame): opening it counts as watching.
  const played = useRef(false);
  const onStart = () => { if (!played.current) { played.current = true; onPlay?.(); } };
  useEffect(() => { if (video?.type === 'youtube') onStart(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const subs = ['fr', 'en'].filter((l) => tut.subtitles[l]);
  const title = tutText(tut, 'title', lang);
  return (
    <div className="modal-backdrop tut-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="tut-player" role="dialog" aria-modal="true" aria-label={title}>
        <div className="tut-player-head">
          <h2>{title}</h2>
          <button ref={closeRef} type="button" className="tut-close" onClick={onClose} aria-label={t('common.close')}><Icon name="close" size={20} /></button>
        </div>
        <div className="tut-frame">
          {video?.type === 'file' && (
            <video ref={ref} src={video.url} poster={poster || undefined} controls playsInline preload="metadata" crossOrigin="anonymous" onPlay={onStart}>
              {subs.map((l) => <track key={l} kind="subtitles" srcLang={l} label={l === 'fr' ? 'Français' : 'English'} src={tut.subtitles[l]} default={l === lang || (subs.length === 1)} />)}
            </video>
          )}
          {video?.type === 'youtube' && (
            <iframe title={title} src={`https://www.youtube-nocookie.com/embed/${video.id}?rel=0&hl=${lang}&cc_lang_pref=${lang}&cc_load_policy=1`}
              allow="encrypted-media; picture-in-picture; fullscreen" allowFullScreen />
          )}
        </div>
        {tutText(tut, 'description', lang) && <p className="tut-player-desc">{tutText(tut, 'description', lang)}</p>}
      </div>
    </div>
  );
}

export function TutorialCard({ tut, onOpen }) {
  const { t, lang } = useI18n();
  const poster = pick(tut.poster, lang);
  const yt = pick(tut.video, lang);
  const img = poster || (yt?.type === 'youtube' ? `https://i.ytimg.com/vi/${yt.id}/hqdefault.jpg` : null);
  return (
    <li className="tut-card">
      <button type="button" onClick={() => onOpen(tut)} aria-haspopup="dialog">
        <span className="tut-thumb">
          {img ? <img src={img} alt="" loading="lazy" /> : <span className="tut-thumb-empty" aria-hidden="true" />}
          <span className="tut-play" aria-hidden="true">▶</span>
          {tut.duration > 0 && <span className="tut-dur">{fmtDuration(tut.duration, t)}</span>}
        </span>
        <span className="tut-card-body">
          <strong>{tutText(tut, 'title', lang)}</strong>
          {tutText(tut, 'description', lang) && <span className="muted small">{tutText(tut, 'description', lang)}</span>}
        </span>
      </button>
    </li>
  );
}
